// Relative + explicit extension: this module is also loaded by `node --test`
// (no Vite alias resolution there).
import type { AppFeedbackReviewStatus } from "breuer-supercharged-sdk";
import {
  formatBackendDate,
  formatInstantInGermanTime,
} from "../../../utils/format_backend_datetime.ts";

/**
 * Pure helpers for the system-admin feedback overview (Admin → Feedback).
 * Separate from the components so Fast Refresh stays happy and `npm test`
 * (node --test) can cover them.
 */

export interface FeedbackCategoryMeta {
  label: string;
  /** antd Tag preset color. */
  color: string;
}

const CATEGORY_META: Record<string, FeedbackCategoryMeta> = {
  bug: { label: "Fehler", color: "red" },
  question: { label: "Frage", color: "blue" },
  idea: { label: "Idee", color: "green" },
};

/** German label + tag color of a feedback category (unknown → raw value). */
export function feedbackCategoryMeta(category: string): FeedbackCategoryMeta {
  return CATEGORY_META[category] ?? { label: category, color: "default" };
}

/** Collapses whitespace and cuts after `max` characters (with „…“). */
export function truncateText(text: string | null | undefined, max: number): string {
  const flat = (text ?? "").replace(/\s+/g, " ").trim();
  return flat.length > max ? `${flat.slice(0, Math.max(0, max - 1))}…` : flat;
}

/**
 * `app_feedback.createdAt` — `DEFAULT now()`, never written by the app, so the
 * stored wall-clock is shown verbatim (case 1 in format_backend_datetime.ts).
 */
export function formatFeedbackCreatedAt(createdAt: string | null | undefined): string {
  return formatBackendDate(createdAt, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export interface MailStatus {
  sent: boolean;
  text: string;
}

/**
 * `app_feedback.emailSentAt` is written by the app as a JS Date (a true
 * instant) → rendered in German time (case 2 in format_backend_datetime.ts).
 */
export function feedbackMailStatus(emailSentAt: string | null | undefined): MailStatus {
  const formatted = formatInstantInGermanTime(emailSentAt);
  return formatted
    ? { sent: true, text: `versendet am ${formatted}` }
    : { sent: false, text: "nicht versendet" };
}

export type DeliveryKind = "sent" | "not_sent" | "in_review" | "discarded";

export interface DeliveryStatus {
  kind: DeliveryKind;
  text: string;
}

interface DeliveryInput {
  reviewStatus?: AppFeedbackReviewStatus | null;
  emailSentAt: string | null | undefined;
}

/**
 * Zustellstatus ans Support-Postfach: leitet sich zuerst aus dem Prüfstatus ab.
 * Offene und verworfene Meldungen haben bewusst keine Mail; nur `direct`/`released`
 * gelten ohne `emailSentAt` als „nicht versendet“.
 */
export function feedbackDeliveryStatus(item: DeliveryInput): DeliveryStatus {
  if (item.reviewStatus === "pending_review") return { kind: "in_review", text: "in Vorprüfung" };
  if (item.reviewStatus === "discarded") return { kind: "discarded", text: "verworfen" };
  const mail = feedbackMailStatus(item.emailSentAt);
  return { kind: mail.sent ? "sent" : "not_sent", text: mail.text };
}

/** Meldungen, die das Support-Postfach hätten erreichen müssen, aber nicht erreicht haben. */
export function countUndeliveredFeedback(items: readonly DeliveryInput[]): number {
  return items.filter((i) => feedbackDeliveryStatus(i).kind === "not_sent").length;
}

/** "1920 × 980 px" or "—" when a dimension is missing. */
export function formatDimensions(
  width: number | null | undefined,
  height: number | null | undefined,
): string {
  return width && height ? `${width} × ${height} px` : "—";
}

/**
 * Short label for the page the feedback was sent from: path + query + hash
 * without the origin ("/offers/123?tab=2"). Falls back to the raw value for
 * anything that is not an absolute URL.
 */
export function pageUrlLabel(pageUrl: string | null | undefined): string {
  if (!pageUrl) return "";
  try {
    const u = new URL(pageUrl);
    return `${u.pathname}${u.search}${u.hash}` || pageUrl;
  } catch {
    return pageUrl;
  }
}

/** Only http(s) links are rendered clickable (no javascript: etc.). */
export function isSafeHttpUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const { protocol } = new URL(url);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

export type LogTone = "error" | "warning" | "info" | "default";

/** Color class of a console level for the log viewer. */
export function logLevelTone(level: string | null | undefined): LogTone {
  switch ((level ?? "").toLowerCase()) {
    case "error":
      return "error";
    case "warn":
    case "warning":
      return "warning";
    case "info":
      return "info";
    default:
      return "default";
  }
}

/**
 * Time of a browser-console entry. The timestamp comes from the reporter's
 * browser (`new Date().toISOString()`, a true instant) → German time, with
 * seconds. Unparseable values are shown as-is.
 */
export function formatLogTime(timestamp: string | null | undefined): string {
  const formatted = formatInstantInGermanTime(timestamp, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  return formatted || (timestamp ?? "");
}
